import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { select, Store } from '@ngrx/store';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  ApiService,
  CoreUiPartialState,
  getBranch,
  getUserId,
  isExternalUser,
  isProgrammeUser,
  saveCustomerOrderDetailsForTask,
} from '@pos-app/core-ui';
import { Router, ActivatedRoute, RoutesRecognized } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { CustomersPartialState } from 'apps/pos-app/src/app/features/customers/+state/customers.reducer';
import { TaskGridActionRendererComponent } from '../../taskActionRender/TaskGridActionRendererComponent';
import { FetchJdeOrder } from 'apps/pos-app/src/app/features/orders/+state/orders.actions';
import { CentralPartialState } from '../../../../+state/central.reducer';
import { FetchSalesPeopleList, FetchSiteList, FetchTaskSummary, LoadTaskDetails } from '../../../../+state/central.actions';
import { centralQuery } from '../../../../+state/central.selectors';

@Component({
  selector: 'app-task-closed',
  templateUrl: './task-closed.component.html',
  styleUrls: ['./task-closed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClosedTaskComponent implements OnInit, OnDestroy {
  @ViewChild('content') modalBox: any;
  public loading$ = new BehaviorSubject<boolean>(false);
  showTextLoader = false;
  searchValue = '';
  frameworkComponents;
  siteList: any[] = [];
  salesPersonList: any[] = [];
  context;
  rowData = [];
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;
  searchTaskForm: FormGroup;
  isShowAdvanceFilter = false;
  taskList: any[] = [];
  private unSubscribe$ = new Subject<void>();
  taskListData$: Observable<any>;
  isExternalUser: boolean;
  isProgrammeUser: boolean;
  defaultBranch: string;
  userEmail: string;
  taskSummary: any;
  isShowTaskAttachments: boolean;
  entityType: string;
  entityId: string;
  taskDetail: any;
  homeSite: any;
  orderNumber: number = this.activatedRoute.snapshot.params.orderNumber;
  orderType: string = this.activatedRoute.snapshot.params.orderType;
  loading = false;
  activeNavIndex = 0;
  hoverNavIndex = -1;
  taskTotalsData: any;
  ticketSummary: any;

  constructor(
    private store: Store<CentralPartialState>,
    private apiService: ApiService,
    private storeCoreUi: Store<CoreUiPartialState>,
    private router: Router,
    private modalService: NgbModal,
    private customerStore: Store<CustomersPartialState>,
    private location: Location,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute
  ) {
    this.frameworkComponents = {
      actionRenderer: TaskGridActionRendererComponent,
    };
    this.context = { componentParent: this };
    this.gridOptions = this.gridOptionsData();
  }

  ngOnInit() {
    this.initializePreExistingData();
    //this.initializeStore();
    this.initializeData();
    this.initilizeFormGroup();

    if ((this.orderNumber && isNaN(this.orderNumber)) || (this.orderType && typeof this.orderType !== 'string')) {
      this.location.back();
    } else {
      if (this.orderNumber && this.orderType) {
        this.showAdvanceFilter();
        this.searchTaskForm.patchValue({ orderNumber: this.orderNumber }, { emitEvent: false });
        this.reloadFilteredTasks();
      } else {
        this.initializeStore();
      }
    }
  }

  // Initialize data from the store
  initializePreExistingData() {
    this.storeCoreUi.pipe(select(getUserId), takeUntil(this.unSubscribe$)).subscribe((userEmail) => {
      this.userEmail = userEmail;
    });
    this.storeCoreUi.pipe(select(getBranch), takeUntil(this.unSubscribe$)).subscribe((branchData) => {
      this.defaultBranch = branchData?.BranchCode;
    });
    this.storeCoreUi.pipe(select(isExternalUser), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.isExternalUser = data;
    });
    this.storeCoreUi.pipe(select(isProgrammeUser), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.isProgrammeUser = data;
    });
  }

  // Dispatch actions to fetch initial data
  initializeStore() {
    this.store.dispatch(new FetchTaskSummary({ showClosedTicketsTF: true }));
    this.store.dispatch(new FetchSiteList());
    this.store.dispatch(new FetchSalesPeopleList());
  }

  // Subscribe to store data and update component state
  initializeData() {
    this.store.pipe(select(centralQuery.getSiteListData), takeUntil(this.unSubscribe$)).subscribe((data) => {
      if (data) {
        this.siteList = data.siteList || [];
        this.homeSite = data.homeSite || null;
      }
    });

    this.store.pipe(select(centralQuery.getSalesPersonList), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.salesPersonList = data || [];
    });

    this.store.pipe(select(centralQuery.getTaskSummaryData), takeUntil(this.unSubscribe$)).subscribe((data) => {
      if (data?.tickets?.length > 0) {
        this.rowData = data?.tickets.map((data) => ({
          ...data,
          isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
        }));
        this.gridApi?.setRowData(this.rowData);
        this.gridApi?.paginationGoToPage(0);
      }
    });
  }

  // Initialize the search form group
  initilizeFormGroup() {
    this.searchTaskForm = new FormGroup({
      searchType: new FormControl(0),
      sourceSite: new FormControl(''),
      assignedSite: new FormControl(''),
      salesPerson: new FormControl(''),
      customer: new FormControl(''),
      assignedTo: new FormControl(''),
      raisedBy: new FormControl(''),
      orderNumber: new FormControl('', [Validators.pattern('^[0-9]*$')]),
      showClosedTicketsTF: new FormControl(true),
    });

    this.searchTaskForm.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$)).subscribe((val) => {
      if (val) {
        if (!this.isShowAdvanceFilter) {
          this.updateFormValues();
          this.reloadFilteredTasks();
        }
      }
    });
  }

  // Update form values based on certain conditions
  updateFormValues() {}

  // Reload tasks based on the filtered form values
  reloadFilteredTasks() {
    const objectToFilter = {};
    for (const key in this.searchTaskForm.value) {
      if (this.searchTaskForm.value[key] === null || this.searchTaskForm.value[key] === '' || key === 'searchType') {
      } else {
        objectToFilter[key] = this.searchTaskForm.value[key];
      }
    }

    this.store.dispatch(new FetchTaskSummary(objectToFilter));
  }

  // Define grid options
  gridOptionsData(): GridOptions {
    return {
      defaultColDef: {
        resizable: true,
        sortable: true,
        width: 150,
        cellClass: 'cell-class ag-right-aligned-cell',
        headerClass: 'header-class ag-left-aligned-header',
        cellStyle: {},
        wrapText: true,
        autoHeight: true,
      },
      columnDefs: [
        // { headerName: 'ACTION', width: 150, cellRenderer: 'actionRenderer', cellStyle: { 'text-align': 'left' } },
        {
          headerName: 'Order',
          field: 'orderNumber',
          width: 100,
          cellStyle: { 'text-align': 'left', 'text-transform': 'none' },
          cellRenderer: (params) => {
            const link = document.createElement('a');
            link.href = 'javascript:void(0)';
            link.innerText = params.value;
            if (params.value > 0) {
              link.addEventListener('click', () => this.fetchOrderFromJDE(params.data));
            } else {
              link.style.pointerEvents = 'none';
              link.style.color = 'grey';
              link.innerText = '';
            }
            return link;
          },
        },
        {
          headerName: 'Description',
          field: 'summary',
          flex: 1,
          minWidth: 200,
          cellStyle: { 'text-align': 'left', 'text-transform': 'none', 'white-space': 'normal', 'word-wrap': 'break-word' },
          cellRenderer: (params) => {
            const link = document.createElement('a');
            link.href = 'javascript:void(0)';
            link.innerText = params.value;
            if (params.data.priority === 'HIGH') {
              const span = document.createElement('span');
              span.style.color = 'red';
              span.innerText = '! ';
              link.prepend(span);
            }
            link.addEventListener('click', () => this.loadTask(params.data));
            return link;
          },
        },
        { headerName: 'Status', field: 'status', width: 100, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        {
          headerName: 'Assigned To',
          field: 'assignedToName',
          width: 150,
          cellStyle: (params) => {
            if (params.value === 'UNASSIGNED') {
              return { 'text-align': 'left', 'text-transform': 'none', 'background-color': ' #DE0A21', color: 'white' };
            }
            return { 'text-align': 'left', 'text-transform': 'none' };
          },
        },
        { headerName: 'Assigned Site', field: 'siteAssignedDescription', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        {
          headerName: 'Due Date',
          field: 'dueDate',
          width: 120,
          cellStyle: (params) => {
            const dueDate = moment(params.value, 'DD/MM/YYYY');
            const currentDate = moment();
            if (dueDate.isSameOrBefore(currentDate, 'day')) {
              return { 'text-align': 'left', 'text-transform': 'none', 'background-color': ' #DE0A21', color: 'white' };
            }
            return { 'text-align': 'left', 'text-transform': 'none' };
          },
        },
        { headerName: 'Created Date', field: 'createDate', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Requestor', field: 'RaisedByName', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Source Site', field: 'siteRaisedDescription', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Last Changed By', field: 'lastChangedByName', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Last Changed Date', field: 'lastUpdated', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
      ],
    };
  }

  // Format date using moment.js
  dateFormatter(params) {
    return moment(params).format('L <HH:mm>');
  }

  // Handle grid ready event
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    const sortModel = [{ colId: 'dateTime', sort: 'desc' }];
    this.gridApi.resetRowHeights();
  }

  // Handle quick filter text box change
  onFilterTextBoxChanged() {
    this.gridApi.setQuickFilter(this.searchValue);
  }

  // Toggle advanced filter visibility
  showAdvanceFilter() {
    this.isShowAdvanceFilter = !this.isShowAdvanceFilter;

    this.rowData = [];
    if (this.isShowAdvanceFilter) {
      this.searchTaskForm.patchValue(
        {
          searchType: 3,
          assignedTo: '',
        },
        { emitEvent: false }
      );
      this.searchTaskForm.get('searchType').disable({ emitEvent: false });
    } else if (!this.isShowAdvanceFilter) {
      this.searchTaskForm.patchValue(
        {
          searchType: 0,
          raised: '',
          unassignedToMySite: '',
          assignedTo: '',
        },
        { emitEvent: false }
      );
      this.clearAdvanceSearch();
      this.searchTaskForm.get('searchType').enable({ emitEvent: false });
      this.updateFormValues();
      this.reloadFilteredTasks();
    }
  }

  // Placeholder for search task functionality
  searchTask() {
    this.reloadFilteredTasks();
  }

  // Clear advanced search form fields
  clearAdvanceSearch() {
    this.searchTaskForm.patchValue(
      {
        sourceSite: '',
        assignedSite: '',
        customer: '',
        orderNumber: '',
        raisedBy: '',
      },
      { emitEvent: false }
    );
  }

  // Load task details and navigate to task detail page
  loadTask(data) {
    this.store.dispatch(new LoadTaskDetails(data.UKID));
    this.router.navigate(['/central/task/detail', data.UKID]);
  }
  navigateToNewTask() {
    this.customerStore.dispatch(saveCustomerOrderDetailsForTask({ customerOrderDetails: { newTask: true } }));
    this.router.navigate(['/central/task/new']);
  }

  // Open modal box
  openModalBox() {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        () => {
          this.isShowTaskAttachments = false;
        },
        () => {
          this.isShowTaskAttachments = false;
        }
      );
  }

  // View task attachments
  viewAttachment(taskDetail) {
    this.taskDetail = taskDetail;
    this.isShowTaskAttachments = true;
    this.entityType = 'crm';
    this.entityId = `${taskDetail.UKID}`;
    this.openModalBox();
  }

  // Placeholder for change request date functionality
  changeRequestDate(data) {}

  // Cleanup subscriptions on component destroy
  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  // Handle modal close event
  handleCloseModal(event) {
    if (event) {
      this.modalService.dismissAll();
    }
  }

  fetchOrderFromJDE(data) {
    this.customerStore.dispatch(
      new FetchJdeOrder({
        orderNumber: data.orderNumber,
        orderType: data.orderType,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      })
    );
  }

  updateRequestDate(newDate: string, data: any) {
    if (newDate === data.requestedDate) {
      return;
    }

    const currentDate = new Date();
    const [day, month, year] = newDate.split('/').map(Number);
    const dateObject = new Date(year, month - 1, day);

    if (dateObject >= currentDate) {
      const dataHeader = {
        dueDate: newDate,
        UKID: data.UKID,
      };

      this.apiService
        .maintainTask(dataHeader)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res) => {
            if (res) {
              this.toastr.success('Request date updated successfully');
            }
          },
          error: (error) => {
            this.toastr.error(error?.ErrorMessage || 'An error occurred while updating the request date');
          },
        });
    } else {
      this.toastr.error("Request date cannot be earlier than today's date");
    }
  }

  goToPreviousScreen() {
    this.location.back();
  }
}
